// MACHINERY V2 (Stroje) — V4's compact STATUS ROW, below 1280 (M2.16b).
//
// Rendered truth: mobile/screenshots/12-v4-phone.png (one column) and
// 13-v4-tablet-1024.png (two columns) — both viewed before this file was
// written. Reference impl: mobile/machinery-mobile.jsx MbV4Row (:383-411);
// values verbatim. The surface itself (fill, border, radius, the 5px bar) is
// wallRowStyle / wallRowBar in wallTileStyles.ts, beside the big tile's.
//
// >>> INERT, LIKE THE TILE. <<< No onClick, no tabIndex, no cursor. The owner
// ruling of 2026-08-24 narrowed the kiosk's blanket "NO interactions" to "no
// interactions on the DATA SURFACE", and this is the data surface at a smaller
// width. The wall's single interactive control is the header's exit X.
//
// >>> THE TRAP THE BOARD ITSELF WALKED INTO. <<< In board 13 the right-hand
// value and the connectivity dot COLLIDE: "pred 6 mi" wraps in the narrower
// 2-column cell and the dot lands on top of the second line ("pred 2 h 14 miÅ"
// on the row below does it too). A relative-time string has no bounded length —
// "pred 2 hodinami a 14 minútami" is a legitimate render — so this is not a
// mock artefact to copy. Three things prevent it here, all on the right block:
//   · it is `flexShrink: 0` with a hard `maxWidth`, so it can neither be
//     squeezed by a long machine name nor grow past its share;
//   · the VALUE is one line, `whiteSpace: nowrap` + ellipsis, with the full
//     string on `title` — it can never wrap onto the dot's line;
//   · the dot sits in its OWN row underneath, `flexShrink: 0`, so even the
//     ellipsised case leaves it a line of its own.
// The identity block carries `minWidth: 0` + `flex: 1`, so it yields first —
// which is what keeps the value in view on a 390px screen.
//
// EVERY PAINT IS FROM THE CONTRACT. deriveCardPresentation() is run ONCE by the
// view and handed down; the board's inline `conn ? … : alarm ? …` tests are not
// ported. In particular the big right-hand value branches on presentation flags
// and `badgeStatus`, never on `status === "running"`.
//
// AND THE SUB-LINE IS THE FLEET'S, NOT THIS ROW'S (review finding F6). Which
// branch a machine falls into is decided by deriveFleetActivity()
// (fleetActivity.ts) — the same call the V1 card's footer, V3's phone row and
// the V3 table's AKTUÁLNE cell make. What stays this surface's own is the
// REGISTER, and it is passed as options rather than forked: the wall prints the
// alarm MESSAGE without its code (the code is already the big value beside it,
// and printing it twice on one row is what a hand-kept copy does), and its
// no-agent word is the terse `wall.noAgent` rather than the card's explainer.
// The `showAlarmStrip && alarms.length > 0` re-test this row carried is gone:
// the flag already folds the length in (cardStates.ts:277) — finding F11.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { getMachineDisplayName } from "../../../../../shared/utils/machineUtils";
import { Machine } from "../../../models/models";
import ConnDot from "../ConnDot";
import { MachineryV2CardPresentation, MachineryV2Telemetry } from "../cardStates";
import { deriveFleetActivity, fleetActivityLine } from "../fleetActivity";
import { wallRowBar, wallRowStyle, wallStatusInk } from "./wallTileStyles";

/** Cap for the right-hand column. Wide enough for "12h 34m" and an alarm code
 *  at 14/700, narrow enough that a 390px row keeps ~215px for the name. */
const VALUE_MAX_WIDTH = 118;

export interface WallStatusRowProps {
  machine: Machine;
  presentation: MachineryV2CardPresentation;
  telemetry: MachineryV2Telemetry;
}

const WallStatusRow: React.FC<WallStatusRowProps> = ({
  machine, presentation: p, telemetry: t,
}) => {
  const { translate } = useTranslation("myMachinery");
  const activity = deriveFleetActivity(p, t);

  // The mono sub-line: alarm message > running program > "bez agenta" >
  // freshness. The board's fourth branch is a free-text job line, which the
  // gateway summary does not carry (fleetTelemetry.ts) — freshness takes its
  // place rather than a fabricated sentence.
  const sub = fleetActivityLine(activity, {
    alarmCode: false,
    noTelemetry: translate("myMachinery:v2.fleet.wall.noAgent"),
    fallback: null,
  });

  // The big right-hand value, in ONE place so the size and the ink cannot drift
  // apart per branch. `wallStatusInk` is the same helper the big tile's numerals
  // use, so a machine reads the same colour on the wall and on the phone. It is
  // NOT the activity line: `running` with an elapsed but no program is a value
  // this row shows and a sub-line branch that does not exist, so the two are
  // deliberately different questions asked of the same derivation.
  const value = activity.kind === "alarm"
    ? { text: activity.alarm.code, size: 14, weight: 700, ink: wallStatusInk(p) }
    : p.badgeStatus === "running" && t.elapsed
    ? { text: t.elapsed, size: 16, weight: 700, ink: wallStatusInk(p) }
    : p.showNoTelemetry
    ? { text: translate("myMachinery:v2.states.unconnected"), size: 8.5, weight: 500, ink: "var(--faint)" }
    : t.heartbeat
    ? { text: t.heartbeat, size: 10.5, weight: 500, ink: "var(--muted)" }
    : null;

  return (
    <div style={wallRowStyle(p)}>
      <span
        aria-hidden
        style={{
          width: 5,
          alignSelf: "stretch",
          borderRadius: 999,
          background: wallRowBar(p),
          flexShrink: 0,
        }}
      />

      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 15, fontWeight: 700, letterSpacing: "-0.01em",
            color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {getMachineDisplayName(machine)}
        </div>
        {sub && (
          <div
            style={{
              fontFamily: "var(--font-mono)", fontSize: 9.5, color: "var(--muted)",
              marginTop: 2, whiteSpace: "nowrap", overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {sub}
          </div>
        )}
      </div>

      {/* See the collision note in this file's header — every property in this
          block is load-bearing against it. */}
      <div
        style={{
          display: "flex", flexDirection: "column", alignItems: "flex-end",
          gap: 3, flexShrink: 0, maxWidth: VALUE_MAX_WIDTH, minWidth: 0,
        }}
      >
        {value && (
          <span
            title={value.text}
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: value.size,
              fontWeight: value.weight,
              color: value.ink,
              letterSpacing: p.showNoTelemetry ? "0.08em" : undefined,
              textTransform: p.showNoTelemetry ? "uppercase" : undefined,
              maxWidth: "100%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {value.text}
          </span>
        )}
        {/* Renders nothing for unconnected / awaiting — `dotConn` is null there
            (cardStates' dotConnFor), which is the §4.3 rule, not a layout
            choice. */}
        <ConnDot state={p.dotConn} size={6} lastHeartbeat={t.heartbeat} />
      </div>
    </div>
  );
};

export default WallStatusRow;
